import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Chip,
  Typography,
  Grid,
  Button,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useSnackbar } from "notistack";
import { deleteVariantOption } from "../api/productApi";

interface Option {
  id?: number;
  name: string;
  values: string[];
  isRequired: boolean;
}

interface OptionsManagerProps {
  options: Option[];
  setOptions: (options: Option[]) => void;
  onOptionsChange: (newOptions: Option[], deletedOptionName?: string) => void;
}

const OptionsManager: React.FC<OptionsManagerProps> = ({
  options,
  setOptions,
  onOptionsChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [newOption, setNewOption] = useState<string>("");
  const [newValues, setNewValues] = useState<Record<number, string>>({});
  const [deleteDialog, setDeleteDialog] = useState<{ 
    open: boolean; 
    index: number | null; 
    optionName: string 
  }>({
    open: false,
    index: null,
    optionName: ""
  });
  const [initialLoad, setInitialLoad] = useState(true);

  // Deduplicate options on initial load
  useEffect(() => {
    if (initialLoad && options.length > 0) {
      const deduplicated = deduplicateOptions(options, false); // Don't show warnings on initial load
      if (deduplicated.length !== options.length) {
        setOptions(deduplicated);
      }
      setInitialLoad(false);
    }
  }, [options, initialLoad]);

  const deduplicateOptions = (opts: Option[], showWarnings = true): Option[] => {
    const seen = new Map<string, number>();
    return opts.filter((option, index) => {
      const key = `${option.name.toLowerCase()}`;
      if (seen.has(key)) {
        if (showWarnings) {
          enqueueSnackbar(`Duplicate option "${option.name}" removed`, { variant: "warning" });
        }
        return false;
      }
      seen.set(key, index);
      return true;
    });
  };

  const handleConfirmedChange = (newOptions: Option[]) => {
    const dedupedOptions = deduplicateOptions(newOptions);
    setOptions(dedupedOptions);
    onOptionsChange(dedupedOptions);
  };

  const handleLocalChange = (newOptions: Option[]) => {
    const dedupedOptions = deduplicateOptions(newOptions);
    setOptions(dedupedOptions);
  };

  const handleAddOption = () => {
    if (newOption.trim()) {
      const isDuplicate = options.some(
        (option) => option.name.toLowerCase() === newOption.trim().toLowerCase()
      );
      
      if (!isDuplicate) {
        const updatedOptions = [
          ...options,
          { name: newOption.trim(), values: [], isRequired: false },
        ];
        handleConfirmedChange(updatedOptions);
        setNewOption("");
        enqueueSnackbar(`Option "${newOption.trim()}" added`, { variant: "success" });
      } else {
        enqueueSnackbar(`Option "${newOption.trim()}" already exists`, { variant: "warning" });
      }
    }
  };

  const handleAddValue = (optionIndex: number, value: string) => {
    if (value.trim()) {
      const updatedOptions = [...options];
      const option = updatedOptions[optionIndex];
      
      const isDuplicate = option.values.some(
        v => v.toLowerCase() === value.trim().toLowerCase()
      );
      
      if (!isDuplicate) {
        option.values.push(value.trim());
        handleConfirmedChange(updatedOptions);
        setNewValues((prev) => ({ ...prev, [optionIndex]: "" }));
        enqueueSnackbar(`Value "${value.trim()}" added to "${option.name}"`, { variant: "success" });
      } else {
        enqueueSnackbar(`Value "${value.trim()}" already exists in "${option.name}"`, { variant: "warning" });
      }
    }
  };

  const handleDeleteValue = (optionIndex: number, valueIndex: number) => {
    const updatedOptions = [...options];
    const deletedValue = updatedOptions[optionIndex].values[valueIndex];
    updatedOptions[optionIndex].values.splice(valueIndex, 1);
    handleConfirmedChange(updatedOptions);
    enqueueSnackbar(`Value "${deletedValue}" deleted`, { variant: "info" });
  };

  const handleRequiredToggle = (optionIndex: number) => {
    const updatedOptions = [...options];
    updatedOptions[optionIndex].isRequired = !updatedOptions[optionIndex].isRequired;
    handleConfirmedChange(updatedOptions);
  };

  const handleDeleteOption = async () => {
    if (deleteDialog.index === null) return;

    const option = options[deleteDialog.index];
    const optionName = option.name;

    try {
      setNewValues(prev => {
        const newValues = {...prev};
        delete newValues[deleteDialog.index!];
        return newValues;
      });

      if (option.id) {
        await deleteVariantOption(option.id);
      }

      const updatedOptions = options.filter((_, idx) => idx !== deleteDialog.index);
      onOptionsChange(updatedOptions, optionName);

      enqueueSnackbar(`Option "${optionName}" and all associated variants deleted`, { 
        variant: "success",
        autoHideDuration: 3000 
      });
    } catch (error) {
      console.error("Delete failed:", error);
      enqueueSnackbar(`Failed to delete option "${optionName}"`, { 
        variant: "error",
        autoHideDuration: 3000 
      });
    } finally {
      setDeleteDialog({ open: false, index: null, optionName: "" });
    }
  };

  const confirmDeleteOption = (index: number) => {
    setDeleteDialog({ 
      open: true, 
      index, 
      optionName: options[index].name 
    });
  };

  return (
    <Box>
      {options.map((option, index) => (
        <Box key={`${option.id}-${option.name}-${index}`} sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {option.name}{" "}
            <Switch
              checked={option.isRequired}
              onChange={() => handleRequiredToggle(index)}
              color="primary"
            />
            <Typography variant="caption" component="span">
              Required
            </Typography>
          </Typography>
          <TextField
            label="Add Value"
            value={newValues[index] || ""}
            onChange={(e) =>
              setNewValues((prev) => ({ ...prev, [index]: e.target.value }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleAddValue(index, newValues[index] || "");
              }
            }}
            fullWidth
            margin="dense"
          />
          <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {option.values.map((value, valueIndex) => (
              <Chip
                key={`${option.id || option.name}-${value}-${valueIndex}`}
                label={value}
                onDelete={() => handleDeleteValue(index, valueIndex)}
              />
            ))}
          </Box>
          <Button
            color="error"
            onClick={() => confirmDeleteOption(index)}
            sx={{ mt: 1 }}
          >
            Delete Option
          </Button>
        </Box>
      ))}
      <Grid container spacing={2} alignItems="center">
        <Grid size={{xs: 8 , sm: 6}}>
          <TextField
            label="New Option Name"
            value={newOption}
            onChange={(e) => setNewOption(e.target.value)}
            fullWidth
            margin="dense"
          />
        </Grid>
        <Grid size={{xs: 2}}>
          <Button 
            onClick={handleAddOption} 
            startIcon={<AddIcon />}
            disabled={!newOption.trim()}
            variant="contained"
          >
            Add Option
          </Button>
        </Grid>
      </Grid>
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, index: null, optionName: "" })}
      >
        <DialogTitle>Delete Option</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to delete the option <strong>"{deleteDialog.optionName}"</strong>?
          </Typography>
          <Typography color="error" variant="body2">
            This will permanently delete all values associated with this option and any variants that depend on it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, index: null, optionName: "" })}>
            Cancel
          </Button>
          <Button 
            color="error" 
            onClick={handleDeleteOption}
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OptionsManager;